import React, { useState, useEffect } from "react";
import {
    Box,
    Input,
    VStack,
    HStack,
    Text,
    Button,
    List,
    ListItem,
    Icon,
    Flex,
    Spinner,
    Badge,
} from "@chakra-ui/react";
import { FaCheckCircle, FaTimesCircle, FaClock, FaUserPlus, FaSearch } from "react-icons/fa";
import { getGroupContacts } from "../../utils/api";

const AttendanceTemplate = ({ questions, answers, handleAnswerChange, setTriggerSurvey, setName, setNumber }) => {
    const [contacts, setContacts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedContact, setSelectedContact] = useState(null);
    const [attendanceStatus, setAttendanceStatus] = useState(null); // 'Present', 'Absent', 'Late'
    const [manualName, setManualName] = useState("");
    const [isManualEntry, setIsManualEntry] = useState(false);

    // Find questions based on JSON structure or keywords
    const listQ = questions?.data?.survey_questions?.find(q =>
        q.question.toLowerCase().includes("list") || q.question_options?.match(/^[0-9a-fA-F-]{36}$/) // Match UUID in options
    );

    // Trigger fetch when listQ is identified and has a valid logic
    useEffect(() => {
        if (listQ) {
            // Priority: Options (as per sample JSON) -> Question Text
            const groupId = listQ.question_options || listQ.question;
            if (groupId) fetchContacts(groupId);
        }
    }, [listQ]);

    const fetchContacts = async (groupId) => {
        setLoading(true);
        const result = await getGroupContacts(groupId);
        const members = result.data?.data?.members_data;
        if (result.success && Array.isArray(members)) {
            // Sort alphabetically ascending (A-Z) by default
            members.sort((a, b) => (a.contact_name || "").localeCompare(b.contact_name || ""));
            setContacts(members);
        } else {
            setContacts([]);
        }
        setLoading(false);
    };

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
        setSelectedContact(null);
        setIsManualEntry(false);
        setAttendanceStatus(null);
    };

    const handleSelectContact = (contact) => {
        setSelectedContact(contact);
        setManualName("");
        setIsManualEntry(false);
        setAttendanceStatus(null);
        setSearchTerm(contact.contact_name || contact.contact_detail); // Update search input with selected name
    };

    const handleManualEntry = () => {
        setIsManualEntry(true);
        setSelectedContact(null);
        setAttendanceStatus(null);
        setManualName(searchTerm); // Use current search term as name
    };

    const handleStatusSelect = (status) => {
        setAttendanceStatus(status);
        updateAnswers(status);
    };

    const generateRandomNumber = () => {
        return Math.floor(10000 + Math.random() * 90000).toString(); // 5 digit number
    };

    const updateAnswers = (status) => {
        const listQ = questions?.data?.survey_questions?.find(q => q.question.toLowerCase().includes("list"));
        const nameQ = questions?.data?.survey_questions?.find(q => q.question.toLowerCase().includes("name"));
        // Status might be missing in some payloads, try to find it, otherwise skip
        const statusQ = questions?.data?.survey_questions?.find(q => q.question.toLowerCase().includes("status"));
        const membershipQ = questions?.data?.survey_questions?.find(q => q.question.toLowerCase().includes("membership"));
        const dateQ = questions?.data?.survey_questions?.find(q =>
            q.question.toLowerCase().includes("date") || q.question_type === "date"
        );

        if (listQ) handleAnswerChange(listQ.question_index, listQ.question_options || listQ.question);

        let finalName = "";
        let membership = "";

        if (selectedContact) {
            finalName = selectedContact.contact_name || selectedContact.contact_detail; // Prioritize name
            membership = "Member";
            if (setName) setName(finalName);
            if (setNumber) setNumber(selectedContact.contact_detail || "");
        } else {
            finalName = manualName;
            membership = "Non-Member"; // Matches "Member,Non-Member" options in JSON
            const randomNum = generateRandomNumber();
            if (setName) setName(finalName);
            if (setNumber) setNumber(randomNum);
        }

        if (nameQ) handleAnswerChange(nameQ.question_index, finalName);
        if (statusQ) handleAnswerChange(statusQ.question_index, status);
        if (membershipQ) handleAnswerChange(membershipQ.question_index, membership);
        if (dateQ) {
            const currentDate = new Date().toISOString().split('T')[0];
            handleAnswerChange(dateQ.question_index, currentDate);
        }
    };

    const filteredContacts = Array.isArray(contacts) ? contacts.filter(contact => {
        const name = contact.contact_name || "";
        const phone = contact.contact_detail || "";
        return name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            phone.includes(searchTerm);
    }) : [];

    return (
        <VStack spacing={6} align="stretch">
            <Text fontSize="lg" fontWeight="bold">Mark Attendance</Text>
            <Text fontSize="sm" color="gray.600">Select their name from the list below</Text>

            <Box>
                <Flex align="center" mb={2}>
                    <Icon as={FaSearch} color="gray.400" mr={2} />
                    <Input
                        placeholder="Search name..."
                        value={searchTerm}
                        onChange={handleSearch}
                    />
                </Flex>

                {loading && <Spinner size="sm" />}

                {!selectedContact && !isManualEntry && (
                    <List spacing={2} maxHeight="200px" overflowY="auto" borderWidth="1px" borderRadius="md">
                        {filteredContacts.map(contact => (
                            <ListItem
                                key={contact.id || contact.contact_detail}
                                p={2}
                                _hover={{ bg: "gray.100", cursor: "pointer" }}
                                onClick={() => handleSelectContact(contact)}
                            >
                                <Text fontWeight="bold">{contact.contact_name}</Text>
                            </ListItem>
                        ))}
                        {filteredContacts.length === 0 && (
                            <ListItem p={2} _hover={{ bg: "gray.100", cursor: "pointer" }} onClick={handleManualEntry}>
                                <Flex align="center" color="blue.500">
                                    <Icon as={FaUserPlus} mr={2} />
                                    <Text>Add "{searchTerm}" as Non-member</Text>
                                </Flex>
                            </ListItem>
                        )}
                    </List>
                )}
            </Box>

            {(selectedContact || isManualEntry) && (
                <Box p={4} borderWidth="1px" borderRadius="md" bg="gray.50">
                    <Text mb={4} fontWeight="bold">
                        Marking for: <Badge colorScheme={selectedContact ? "green" : "orange"}>
                            {selectedContact ? selectedContact.contact_name : manualName}
                        </Badge>
                    </Text>

                    <HStack spacing={4} justify="center">
                        <StatusButton
                            status="Present"
                            icon={FaCheckCircle}
                            color="green"
                            current={attendanceStatus}
                            onClick={() => handleStatusSelect("Present")}
                        />
                        <StatusButton
                            status="Absent"
                            icon={FaTimesCircle}
                            color="red"
                            current={attendanceStatus}
                            onClick={() => handleStatusSelect("Absent")}
                        />
                        <StatusButton
                            status="Late"
                            icon={FaClock}
                            color="yellow"
                            current={attendanceStatus}
                            onClick={() => handleStatusSelect("Late")}
                        />
                    </HStack>
                </Box>
            )}
        </VStack>
    );
};

const StatusButton = ({ status, icon, color, current, onClick }) => (
    <VStack
        as="button"
        onClick={onClick}
        p={4}
        borderRadius="md"
        borderWidth="2px"
        borderColor={current === status ? `${color}.500` : "transparent"}
        bg={current === status ? `${color}.50` : "white"}
        _hover={{ bg: `${color}.50` }}
        transition="all 0.2s"
    >
        <Icon as={icon} boxSize={8} color={`${color}.500`} />
        <Text fontWeight="medium">{status}</Text>
    </VStack>
);

export default AttendanceTemplate;
